import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  styled,
  TableCell,
  tableCellClasses,
  Pagination,
  // Pagination,
  // TextField,
  // InputAdornment,
} from '@mui/material';
import InviteUserDrawer from './InviteUserDrawer';
import { deleteUser, getUser } from '../../api/user';
import { ReactComponent as EditIcon } from '../../assets/svg/edit.svg';
import { ReactComponent as Trash } from '../../assets/svg/trash.svg';
import { showErrorToast, showSuccessToast } from '../../toastUtils';
import CommonConfirmationModal from '../../CommonComponents/CommonConfirmationModal';
import CommonHeader from '../../CommonComponents/CommonHeader';
import ButtonCommon from '../../CommonComponents/ButtonCommon';
import { Add } from '@mui/icons-material';
import SearchInput from '../../CommonComponents/SearchInput';
import ComponentWrapper from '../../CommonComponents/ComponentWrapper';
import { useUser } from '../../components/UserContext';

export default function UserManagement() {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#F9FAFB",
      color: "#000000",
      fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const { currentUser } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState([]);
  const [user, setUser] = useState({});
  const [userDelete, setUserDelete] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await getUser(page, pageSize);
      if (response.success) {
        setUserData(response.data.data || []);
        setFilteredData(response.data.data || []);
        setPaginatedData(response.data.data || []);
        setTotalCount(response.data.total_count || 0);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setPaginatedData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setUser({});
    setIsOpen(false);
  };

  const handleDeleteClick = (user) => {
    setUserDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setUserDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!userDelete.user_id) {
      console.error('User ID is required for deletion');
      return;
    }

    setDeleteLoading(true)
    try {
      const response = await deleteUser(userDelete.user_id);
      if (response.success) {
        // setUserData((prev) => prev.filter((user) => user.user_id !== userDelete.user_id));
        fetchUserData();
        showSuccessToast(response.message);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      showErrorToast(error.message);
    }
    finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setUserDelete(null);
    }
  };

  useEffect(() => {
    let data = paginatedData;
    const filtered = data?.filter((item) => {
      const isNameMatch = item.full_name.toLowerCase().includes(searchValue.toLowerCase());
      return isNameMatch;
    });

    setFilteredData(filtered);
  }, [searchValue, userData, paginatedData]);

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: { xs: '100%', md: '75%' } }}> */}
      {/* <Stack sx={{ maxWidth: '1000px' }}> */}
      <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: 'column', mb: 3 }}>
        <CommonHeader
          title="User Management"
          subTitle="You can add, update or delete users on this page."
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 2 }}>
          <SearchInput
            placeholder="Search by Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <ButtonCommon icon={<Add />} onClick={() => setIsOpen(true)}>
            Invite A User
          </ButtonCommon>
        </Box>
      </Box>

      <Box>
        <TableContainer component={Paper}
          variant="outlined"
          sx={{ borderRadius: "12px" }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Email Address</StyledTableCell>
                <StyledTableCell>Role</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                filteredData && filteredData.length > 0 ? (
                  filteredData.map((row) => {
                    // const initials = row.full_name
                    //   ? row.full_name
                    //     .split(' ')
                    //     .map((n) => n[0])
                    //     .join('')
                    //     .toUpperCase()
                    //   : '?';
                    const initials = row.full_name.charAt(0).toUpperCase()

                    return (
                      <TableRow key={row.user_id || row.email} hover
                        sx={
                          currentUser.email === row.email
                            ? {
                              pointerEvents: 'none', // Disables interaction
                              opacity: 0.5,          // Fades the row
                              backgroundColor: '#f5f5f5',
                            }
                            : {}
                        }
                      >
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                backgroundColor: '#FFE5DD',
                                color: '#FF6348',
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '14px',
                              }}
                            >
                              {initials}
                            </Box>
                            <Typography sx={{ fontSize: '14px' }}>{row.full_name}</Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontSize: '14px' }}>{row.email}</StyledTableCell>
                        <StyledTableCell sx={{ fontSize: '14px' }}>{row.role}</StyledTableCell>
                        <StyledTableCell sx={{ px: 0 }}>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              onClick={() => {
                                setUser(row);
                                setIsOpen(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton onClick={() => handleDeleteClick(row)}>
                              {/* handleDeleteUser(row.user_id) */}
                              <Trash />
                            </IconButton>
                          </Tooltip>
                        </StyledTableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={4}>
                      <Typography
                        sx={{
                          textAlign: 'center',
                          py: 2,
                          color: 'text.secondary',
                          fontSize: '14px',
                        }}
                      >
                        No data available
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
            variant="outlined"
          />
        </Box>
      </Box>


      <InviteUserDrawer isOpen={isOpen} onClose={handleClose} setUserData={setUserData} user={user} fetchUserData={fetchUserData} />

      <CommonConfirmationModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        description={`Are you sure you want to delete?`}
        loading={deleteLoading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
      {/* </Stack> */}
    </ComponentWrapper>
  );
}
